import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { plural } from "@/lib/plural";
import { FileSpreadsheet, CheckCircle2, AlertTriangle, Upload } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <span className="text-body text-muted-foreground">{label}</span>
    <span className="text-body tabular-nums text-foreground">{value}</span>
  </div>
);

const Block = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="text-label font-semibold uppercase tracking-[0.07em] text-muted-foreground">{title}</h3>
    <div className="mt-1.5">{children}</div>
  </div>
);

/** Objecten: the register itself -- locaties, ruimtes, machines. */
function ObjectImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  // The destructive option is off by default: a routine re-import must not
  // delete a machine the TD typed in himself.
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [keepVehicles, setKeepVehicles] = useState(true);
  const [done, setDone] = useState(null);
  // The flags the shown report was COMPUTED with. Doorvoeren sends those, so it
  // can never do something the report on screen did not show.
  const [reportFlags, setReportFlags] = useState(null);

  const preview = async (f, flags) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("replace_existing", flags.replace ? "true" : "false");
      fd.append("keep_vehicles", flags.vehicles ? "true" : "false");
      const { data } = await api.post("/imports/mcmain/objects/preview", fd, { timeout: 0 });
      setReport(data);
      setReportFlags(flags);
    } catch (err) {
      toast.error(getErrorMessage(err, "Voorbeeld mislukt."));
    } finally {
      setBusy(false);
    }
  };

  // Changing a switch changes what would happen, so the report on screen is no
  // longer about this run: it is dropped, not silently kept.
  const setFlag = (key, value) => {
    if (key === "replace") setReplaceExisting(value); else setKeepVehicles(value);
    setReport(null);
    setReportFlags(null);
    setDone(null);
    if (file) {
      preview(file, {
        replace: key === "replace" ? value : replaceExisting,
        vehicles: key === "vehicles" ? value : keepVehicles,
      });
    }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setReport(null);
    setReportFlags(null);
    setDone(null);
    await preview(f, { replace: replaceExisting, vehicles: keepVehicles });
  };

  const commit = async () => {
    if (!file || !reportFlags) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("replace_existing", reportFlags.replace ? "true" : "false");
      fd.append("keep_vehicles", reportFlags.vehicles ? "true" : "false");
      const { data } = await api.post("/imports/mcmain/objects/commit", fd, { timeout: 0 });
      setDone(data);
      toast.success(`Klaar: ${data.counts.new} nieuw, ${data.counts.unchanged} ongewijzigd`);
      setReport(null);
      setReportFlags(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Dat lukte niet."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="mcmain-objects-card">
      <h2 className="text-body font-semibold text-foreground">Objecten uit McMain</h2>
      <p className="mt-1 text-body text-muted-foreground">
        Het register komt uit McMain: locatie, ruimte, machine. Je ziet eerst wat er zou gebeuren,
        inclusief wat er is rechtgetrokken. Er wordt niets opgeslagen tot je op doorvoeren drukt.
      </p>

      <Separator className="my-4" />

      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="mc-replace">Machines die niet uit McMain komen weghalen</Label>
            <p className="text-data text-muted-foreground">
              Alles wat aan werk vast zit blijft staan; dat zie je terug in het verslag.
            </p>
          </div>
          <Switch id="mc-replace" data-testid="mcmain-replace-switch" checked={replaceExisting} onCheckedChange={(v) => setFlag("replace", v)} />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="mc-vehicles">Bedrijfswagens houden</Label>
            <p className="text-data text-muted-foreground">
              Ze hangen onder TW1G, dus zonder locatie, en blijven daarna in de app te verplaatsen.
            </p>
          </div>
          <Switch id="mc-vehicles" data-testid="mcmain-vehicles-switch" checked={keepVehicles} onCheckedChange={(v) => setFlag("vehicles", v)} />
        </div>
      </div>

      <Separator className="my-4" />

      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="mcmain-objects-file" onChange={onPick} />
        <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
          <FileSpreadsheet size={18} className="mr-2" /> Kies eqExport.xlsx
        </Button>
        {file && <span className="text-body text-muted-foreground">{file.name}</span>}
      </div>

      {busy && <p className="mt-3 text-body text-muted-foreground">Bezig met lezen...</p>}

      {done && (
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-3" data-testid="mcmain-objects-done">
          <div className="flex items-center gap-2 text-body text-foreground">
            <CheckCircle2 size={18} className="text-goed-tekst" /> Doorgevoerd
          </div>
          <Row label="Nieuwe machines" value={done.counts.new} />
          <Row label="Ongewijzigd" value={done.counts.unchanged} />
          {done.counts.manual > 0 && (
            <Row label="Handmatig aangepast in TD-Ops, blijft staan" value={done.counts.manual} />
          )}
          <Row label="Nieuwe locaties" value={done.counts.locations} />
          <Row label="Nieuwe ruimtes" value={done.counts.ruimtes_new} />
          {done.counts.linked > 0 && (
            <Row label="Aan een bestaande bedrijfswagen gekoppeld" value={done.counts.linked} />
          )}
          <Row label="Weggehaald" value={done.counts.removed} />
          <Row label="Behouden" value={done.counts.kept} />
          {done.manual?.length > 0 && (
            <Block title="Bleef staan zoals het in TD-Ops stond">
              <ul className="space-y-1">
                {done.manual.map((m) => (
                  <li key={m.asset_code} className="text-body text-foreground">
                    <span className="font-mono text-data">{m.asset_code}</span>{" "}
                    <span className="text-muted-foreground">{m.fields.join(", ")} wijkt af van het bestand</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}
          {done.ruimte_notes?.length > 0 && (
            <Block title="Ruimtes">
              <ul className="space-y-1">
                {done.ruimte_notes.map((n, i) => (
                  <li key={`${n.ruimte}-${i}`} className="text-body text-foreground">
                    {n.ruimte} <span className="text-muted-foreground">— {n.note}</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}
          {done.blocked?.length > 0 && (
            <Block title="Bleef staan, er hangt werk aan">
              <ul className="space-y-1">
                {done.blocked.map((b) => (
                  <li key={b.asset_code} className="text-body text-foreground">
                    <span className="font-mono text-data">{b.asset_code}</span> — {b.reason}
                  </li>
                ))}
              </ul>
            </Block>
          )}
        </div>
      )}

      {report && (
        <div className="mt-4" data-testid="mcmain-objects-report">
          {report.valid === false ? (
            <div className="flex items-start gap-2 text-body text-letop-tekst">
              <AlertTriangle size={18} className="mt-0.5" />
              <div>{(report.errors || []).join(" ")}</div>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-border p-3">
                <Row label="Regels in het bestand" value={report.totals.rows} />
                <Row label="Locaties" value={report.totals.sites} />
                <Row label="Ruimtes" value={report.totals.ruimtes} />
                <Row label="Machines" value={report.totals.assets} />
                <Separator className="my-2" />
                <Row label="Nieuw" value={report.counts.new} />
                <Row label="Ongewijzigd" value={report.counts.unchanged} />
                {report.counts.manual > 0 && (
                  <Row label="Handmatig aangepast in TD-Ops, blijft staan" value={report.counts.manual} />
                )}
                {report.counts.linked > 0 && (
                  <Row label="Aan een bestaande bedrijfswagen gekoppeld" value={report.counts.linked} />
                )}
                <Row label="Gaat weg" value={report.removals.count} />
                <Row label="Blijft staan" value={report.removals.kept} />
                {report.removals.locked > 0 && (
                  <Row label="Blijft staan, er hangt werk aan" value={report.removals.locked} />
                )}
              </div>

              <Block title="Locaties">
                <div className="divide-y divide-border">
                  {report.locations.map((l) => (
                    <div key={l.source_id} className="py-1.5">
                      <div className="text-body text-foreground">
                        {l.source_name}
                        {l.action === "matched" && <span className="text-muted-foreground"> — bestaat al</span>}
                        {l.action === "new" && <span className="text-muted-foreground"> — nieuw</span>}
                        {l.action === "skipped" && <span className="text-muted-foreground"> — niet overgenomen</span>}
                      </div>
                      {l.note && <div className="text-data text-muted-foreground">{l.note}</div>}
                    </div>
                  ))}
                </div>
              </Block>

              {report.sporen?.length > 0 && (
                <Block title="Sporen per was-hal">
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Locatie</TableHead>
                          <TableHead>Sporen</TableHead>
                          <TableHead>Let op</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.sporen.map((s) => (
                          <TableRow key={s.location_id}>
                            <TableCell className="text-body">{s.location_name}</TableCell>
                            <TableCell className="text-body">{s.sporen.join(", ")}</TableCell>
                            <TableCell className="text-data text-muted-foreground">{s.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="divide-y divide-border sm:hidden">
                    {report.sporen.map((s) => (
                      <div key={s.location_id} className="py-1.5">
                        <div className="text-body text-foreground">{s.location_name}</div>
                        <div className="text-data text-muted-foreground">{s.sporen.join(", ")}</div>
                        {s.note && <div className="text-data text-letop-tekst">{s.note}</div>}
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {report.vehicles?.in_register > 0 && (
                <Block title="Bedrijfswagens">
                  <div className="text-body text-foreground">
                    {plural(report.vehicles.in_register, "bedrijfswagen", "bedrijfswagens")} in TD-Ops,{" "}
                    {report.vehicles.in_file} in dit bestand. Ze blijven allemaal staan.
                  </div>
                  {report.vehicles.linked?.length > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {report.vehicles.linked.map((v) => (
                        <li key={v.source_id} className="text-data text-muted-foreground">
                          <span className="text-body text-foreground">{v.asset_code}</span> {v.name}{" "}
                          ({v.registration}) is McMain-object {v.source_id} — gekoppeld, geen tweede regel
                        </li>
                      ))}
                    </ul>
                  )}
                </Block>
              )}

              {report.categories?.length > 0 && (
                <Block title="Categorieën">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {report.categories.map((c) => (
                      <span key={c.category} className="text-body text-foreground">
                        {c.category} <span className="tabular-nums text-muted-foreground">{c.count}</span>
                      </span>
                    ))}
                  </div>
                </Block>
              )}

              {report.normalised?.length > 0 && (
                <Block title="Rechtgetrokken">
                  <ul className="space-y-1.5">
                    {report.normalised.map((n) => (
                      <li key={n.what}>
                        <div className="text-body text-foreground">{plural(n.count, "keer", "keer")} {n.what}</div>
                        <div className="text-data text-muted-foreground">{n.examples.join(" · ")}</div>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              {report.attention?.length > 0 && (
                <Block title="Let op">
                  <ul className="space-y-2">
                    {report.attention.map((a, i) => (
                      <li key={`${a.what}-${i}`}>
                        <div className="text-body text-foreground">
                          {a.count !== undefined ? `${a.count}x ` : ""}{a.what}
                        </div>
                        {a.detail && <div className="text-data text-muted-foreground">{a.detail}</div>}
                        {a.items?.length > 0 && (
                          <div className="text-data text-muted-foreground">{a.items.join(" · ")}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

              <div className="mt-5">
                <Button data-testid="mcmain-objects-commit" disabled={busy} onClick={commit}>
                  <Upload size={18} className="mr-2" /> Doorvoeren
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/** Artikelen: the parts stock (Onderdelen). */
function ArticleImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const post = async (path, setter) => {
    if (!file) return null;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(path, fd, { timeout: 0 });
      setter(data);
      return data;
    } catch (err) {
      toast.error(getErrorMessage(err, "Dat lukte niet."));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // A failed preview of this file must not leave the previous file's report
    // standing next to the new filename.
    setReport(null);
    setDone(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/imports/mcmain/articles/preview", fd, { timeout: 0 });
      setReport(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Voorbeeld mislukt."));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    const data = await post("/imports/mcmain/articles/commit", setDone);
    if (data) {
      toast.success(`Klaar: ${data.counts.new} nieuw, ${data.counts.updated} bijgewerkt`);
      setReport(null);
    }
  };

  return (
    <Card className="p-5" data-testid="mcmain-articles-card">
      <h2 className="text-body font-semibold text-foreground">Artikelen uit McMain</h2>
      <p className="mt-1 text-body text-muted-foreground">
        De voorraad achter Onderdelen: naam, bestelnummer, leverancier, aantal en bestelpunt.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="mcmain-articles-file" onChange={onPick} />
        <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
          <FileSpreadsheet size={18} className="mr-2" /> Kies itemExport.xlsx
        </Button>
        {file && <span className="text-body text-muted-foreground">{file.name}</span>}
      </div>

      {done && (
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-3" data-testid="mcmain-articles-done">
          <div className="flex items-center gap-2 text-body text-foreground">
            <CheckCircle2 size={18} className="text-goed-tekst" /> Doorgevoerd
          </div>
          <Row label="Nieuw" value={done.counts.new} />
          <Row label="Bijgewerkt" value={done.counts.updated} />
          <Row label="Ongewijzigd" value={done.counts.unchanged} />
          {done.counts.stock_changed > 0 && (
            <Row label="Aantal of bestelpunt gewijzigd" value={done.counts.stock_changed} />
          )}
          {done.stock_changes?.length > 0 && (
            <Block title="Voorraad die McMain anders telt">
              <ul className="space-y-1">
                {done.stock_changes.map((s) => (
                  <li key={s.sku} className="text-body text-foreground">
                    <span className="font-mono text-data">{s.sku}</span> {s.name}{" "}
                    <span className="text-muted-foreground">
                      {s.from[0]} → {s.to[0]} (bestelpunt {s.from[1]} → {s.to[1]})
                    </span>
                  </li>
                ))}
              </ul>
            </Block>
          )}
        </div>
      )}

      {report && (
        <div className="mt-4" data-testid="mcmain-articles-report">
          {report.valid === false ? (
            <div className="flex items-start gap-2 text-body text-letop-tekst">
              <AlertTriangle size={18} className="mt-0.5" />
              <div>{(report.errors || []).join(" ")}</div>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-border p-3">
                <Row label="Artikelen in het bestand" value={report.totals.rows} />
                <Row label="Nieuw" value={report.counts.new} />
                <Row label="Bijgewerkt" value={report.counts.updated} />
                <Row label="Ongewijzigd" value={report.counts.unchanged} />
              </div>
              {report.groups?.length > 0 && (
                <Block title="Groepen">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {report.groups.map((g) => (
                      <span key={g.group} className="text-body text-foreground">
                        {g.group} <span className="tabular-nums text-muted-foreground">{g.count}</span>
                      </span>
                    ))}
                  </div>
                </Block>
              )}
              {report.notes?.length > 0 && (
                <Block title="Let op">
                  <ul className="space-y-2">
                    {report.notes.map((n) => (
                      <li key={n.what}>
                        <div className="text-body text-foreground">{n.count}x {n.what}</div>
                        <div className="text-data text-muted-foreground">{n.detail}</div>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}
              <div className="mt-5">
                <Button data-testid="mcmain-articles-commit" disabled={busy} onClick={commit}>
                  <Upload size={18} className="mr-2" /> Doorvoeren
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

export function SettingsMcMain() {
  return (
    <div className="space-y-4">
      <ObjectImport />
      <ArticleImport />
    </div>
  );
}
